<template>
  <MainLayout>
    <div class="container mw-960" slot="action-bar">
      <div class="row">
        <div class="col-md-4 col-lg-4 col-xl-4">
          <CardTitleInput class="action-header typography--headline typography--title" v-bind:model-value="card.title" v-on:input="applyCardTitle" v-on:has-error="onHasError"></CardTitleInput>
        </div>

        <div class="col-md-8 col-lg-8 col-xl-8 hidden-sm-down">
          <div class="action-btn-row">
            <MoreBtnDropdown class="float-right">
              <b-dropdown-item class="dropdown-item typography--color-secondary"
                               v-on:click="$root.$emit('show::modal', 'delete-my-card-modal')">{{ $t('common.card.menu.remove') }}</b-dropdown-item>
              <div v-if="this.isBusanPublisher">
                <div class="dropdown-divider"></div>
                <div class="dropdown-label" v-bind:style="{'padding-left': '20px'}">
                  <strong>부산교육청 메뉴</strong>
                </div>
                <b-dropdown-item class="dropdown-item" v-on:click="$root.$emit('show::modal', 'export-content')">
                  BEST로 내보내기
                </b-dropdown-item>
              </div>
            </MoreBtnDropdown>
            <button class="btn btn-primary float-right ml-1 mr-1" v-on:click="saveMyCard" v-bind:disabled="saveDisabled || !this.isModified">
              <span v-if="!busy">{{ $t('common.save') }}</span>
              <LoadingBounce v-else></LoadingBounce>
            </button>


            <button class="btn btn-outline-primary float-right mr-1" v-on:click="openCardPreview">{{ $t('common.preview') }}</button>

            <button
              class="btn btn-outline-primary float-right ml-1 mr-1"
              @click="handleMyCardShareSetupClick(card.id, card.share_level)"
              v-bind:disabled="saveDisabled"
              style="padding: 5px 16px;"
            >
              <img src="/static/images/ic-hotspot.svg" />
              <span v-if="card.share_level === 2">비공개중</span>
              <span v-if="card.share_level === 1">전체 공개중</span>
              <span v-if="card.share_level === 3">기관 공개중</span>
              <span v-if="card.share_level === 99">숨김처리중</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-960 mt-4 pt-1" slot="layout-body">
      <div class="row">
        <div class="col-md-4">
          <div class="card-row">
            <Card class="library-detail-card">
              <div class="card-cover-img-thumb" v-bind:style="styleBindings"></div>

              <label class="card-cover-img-input text-center clickable">
                <img src="/static/images/ic-camera.svg" alt="">
                <input type="file" class="no-display" accept="image/png,image/jpeg,image/jpg" v-on:change="onCoverImageChange">
              </label>

              <div class="card-block">
                <div class="card-info-section">
                  <div class="row" v-if="!card.subject_id">
                    <div class="col-12 typography--body-3 typography--color-disabled">
                      {{ $t('card_details.card_meta.subject_placeholder') }}
                    </div>
                  </div>

                  <div class="row" v-else>
                    <div class="col-4">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.subject') }}
                      </small>
                    </div>
                    <div class="col-8">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getSubjectLabel(card) }}
                      </small>
                    </div>
                  </div>


                  <div class="row" v-if="card.grade_id === undefined">
                    <div class="col-12 typography--body-3 typography--color-disabled">
                      {{ $t('card_details.card_meta.grade_placeholder') }}
                    </div>
                  </div>

                  <div class="row" v-else>
                    <div class="col-4">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.grade') }}
                      </small>
                    </div>
                    <div class="col-8">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getGradeLabel(card) }}
                      </small>
                    </div>
                  </div>

                  <!-- Unit -->
                  <div class="row" v-if="!card.unit_id">
                    <div class="col-12 typography--body-3 typography--color-disabled">
                      {{ $t('card_details.card_meta.chapter_placeholder') }}
                    </div>
                  </div>

                  <div class="row" v-if="card.unit_id">
                    <div class="col-4">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.chapter') }}
                      </small>
                    </div>
                    <div class="col-8">
                      <small class="typography--body-3 typography--color-primary">
                        {{ card.chapter_name }}
                      </small>
                    </div>
                  </div>

                  <!-- Core Concept -->
                  <div class="row" v-if="card.core_concept_id === undefined || card.core_concept_id === null">
                    <div class="col-12 typography--body-3 typography--color-disabled">
                      {{ $t('card_details.card_meta.core_concepts_placeholder') }}
                    </div>
                  </div>

                  <div class="row" v-else>
                    <div class="col-4">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.core_concept') }}
                      </small>
                    </div>
                    <div class="col-8">
                      <small class="typography--body-3 typography--color-primary" v-if="isPublisherUser">
                        {{ `${getCoreConceptLabel(coreConcepts[card.core_concept_id])} (${card.core_concept_id})` }}
                      </small>
                      <small class="typography--body-3 typography--color-primary" v-else>
                        {{ getCoreConceptLabel(coreConcepts[card.core_concept_id]) }}
                      </small>
                    </div>
                  </div>
                </div>

                <div class="card-keywords-section">
                  <div class="row" v-if="card.core_concept_id">
                    <div class="col-4">
                      <small class="typography--caption typography--color-secondary">
                        {{ $t('card_details.card_meta.learning_elements') }}
                      </small>
                    </div>
                    <div class="col-8">
                      <small class="typography--body-3 typography--color-primary">
                        {{ getLearningElements(coreConcepts[card.core_concept_id]) }}
                      </small>
                    </div>
                  </div>
                </div>

              </div>

              <div class="card-action">
                <button
                  class="btn btn-outline-primary btn-block"
                  :disabled="isLoadingVideoCourseCard"
                  @click="$root.$emit('show::modal', 'edit-card-modal')"
                >
                  <template v-if="isLoadingVideoCourseCard">
                   <LoadingBounce />
                  </template>
                  <template v-else>
                    {{ $t('card_details.card_meta.edit') }}
                  </template>
                </button>

                <b-modal id="edit-card-modal"
                         v-bind:title="$t('card_details.card_meta.edit')"
                         class="edit-card-modal"
                         v-on:ok="applySelectedCardInfo"
                         v-on:shown="setupEditCardModal"
                         v-bind:ok-title="$t('common.save')"
                         v-bind:close-title="$t('common.cancel')">

                  <div class="dropdown-group">
                    <div class="form-group">
                      <label for="card-title-input" class="typography--body-3 typography--color-secondary">
                        <strong>{{ $t('card_details.card_meta.title') }}</strong>
                      </label>
                      <input type="text"
                             class="form-control basic-text-input"
                             id="card-title-input"
                             v-bind:placeholder="$t('card_details.card_meta.title_placeholder')"
                             maxlength="50"
                             v-model="selected.title">
                    </div>
                  </div>

                  <div class="dropdown-group" v-if="isPublisherUser">
                    <div class="form-group">
                      <label for="card-goal-input" class="typography--body-3 typography--color-secondary">
                        <strong>{{ $t('card_details.card_meta.goal') }}</strong>
                      </label>
                      <AutosizeTextarea
                        id="card-goal-input"
                        class="form-control basic-text-input"
                        v-model="selected.goal"
                        v-bind:rows="3"
                        v-bind:limit="5000"
                        v-bind:placeholder="$t('card_details.card_meta.goal_placeholder')"
                      />
                    </div>
                  </div>

                  <div class="dropdown-group">
                    <div class="dropdown-label typography--body-3 typography--color-secondary">
                      <strong>{{ $t('card_details.card_meta.grade') }}</strong>
                    </div>
                    <b-dropdown
                      :text="getTranslatedName(gradeMap[selected.grade_id])"
                      class="edit-card-dropdown"
                    >
                      <b-dropdown-item
                        v-for="(value, key) in gradeList"
                        @click="selectGrade(value.id, value.school_year)"
                        :key="key"
                      >
                        {{ getTranslatedName(gradeMap[value.id]) }}
                      </b-dropdown-item>
                      <b-dropdown-item @click="selectGrade(null, 'null')">
                        {{ $t('common.selects.others') }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <div class="dropdown-group">
                    <div class="dropdown-label typography--body-3 typography--color-secondary">
                      <strong>{{ $t('card_details.card_meta.subject') }}</strong>
                    </div>
                    <b-dropdown
                      id="subject-dropdown"
                      :text="getTranslatedName(subjectMap[selected.subject_id])"
                      class="edit-card-dropdown"
                    >
                      <b-dropdown-item
                        v-for="(value, key) in gradeSubjects"
                        @click="selectSubject(value.id)"
                        :key="key"
                      >
                        <template v-if="value.id">
                          {{ getTranslatedName(subjectMap[value.id]) }}
                        </template>
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <!-- Units -->
                  <div class="dropdown-group">
                    <div class="dropdown-label typography--body-3 typography--color-secondary"><strong>{{ $t('card_details.card_meta.chapter') }}</strong></div>

                    <b-dropdown v-bind:text="getUnitLabel(units[selected.unit_id]) || $t('card_details.card_meta.select')" class="edit-card-dropdown" id="unit-dropdown">
                      <b-dropdown-item v-on:click="selectUnit('9999')">
                        {{ $t('card_details.card_meta.select') }}
                      </b-dropdown-item>

                      <b-dropdown-item v-for="unit in currentUnitList"
                                       v-on:click="selectUnit(unit.id)"
                                       v-bind:key="unit.id">
                        {{ getUnitLabel(unit) }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <!-- Core Concepts -->
                  <div class="dropdown-group">
                    <div class="dropdown-label typography--body-3 typography--color-secondary"><strong>{{ $t('card_details.card_meta.core_concept') }}</strong></div>

                    <b-dropdown v-bind:text="getDetailedCoreConceptLabel(coreConcepts[selected.core_concept_id]) || $t('card_details.card_meta.select')" class="edit-card-dropdown" id="core-concept-dropdown">
                      <b-dropdown-item v-on:click="selectCoreConcept('9999')">
                        {{ $t('card_details.card_meta.select') }}
                      </b-dropdown-item>

                      <b-dropdown-item v-for="coreConcept in currentCoreConceptList"
                                      v-on:click="selectCoreConcept(coreConcept.id)"
                                      v-bind:key="coreConcept.id"
                                      v-bind:title="`${$t('card_details.card_meta.learning_elements')}: ${coreConcept.description}`">
                        {{ getDetailedCoreConceptLabel(coreConcept) }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <div class="keyword-group">
                    <div class="keyword-label typography--body-3 typography--color-secondary">
                      <strong>{{ $t('card_details.card_meta.learning_elements') }}</strong>
                    </div>
                    <div class="keyword-container">
                      <small class="typography--body-3 typography--color-secondary" v-if="coreConcepts[selected.core_concept_id]">
                        {{ getLearningElements(coreConcepts[selected.core_concept_id]) }}
                      </small>
                      <small class="typography--body-3 typography--color-disabled" v-else>
                        -
                      </small>
                    </div>
                  </div>

                  <div v-if="isPublisherUser" class="dropdown-group">
                    <div class="form-group">
                      <label
                        for="video-course-id"
                        class="typography--body-3 typography--color-secondary"
                      >
                        <strong>
                          {{ $t('card_details.card_meta.video_course') }}
                        </strong>
                      </label>
                      <input
                        type="text"
                        class="form-control basic-text-input"
                        id="video-course-id"
                        v-model="selected.video_course_id"
                      />
                    </div>
                  </div>

                </b-modal>
              </div>

            </Card>
          </div>
        </div>


        <div class="col-md-8" v-show="!pagesReady">
          <div class="card-row">
            <DummyPageSummary></DummyPageSummary>
          </div>
        </div>

        <div class="col-md-8" v-show="pagesReady">
          <draggable v-model="card.pages" @start="drag=true" @end="drag=false">
            <div
              class="card-row"
              v-for="(page, index) in card.pages"
              :key="index"
            >
              <MyPageSummary v-bind:page="page"
                            v-bind:card="card"
                            v-bind:index="index"
                            v-bind:card-title="card.title"
                            v-on:edit-page="openPageEditorModal"
                            v-on:remove-page="openRemovePageModal"
                            v-on:hidden-page="openHiddenPageModal"
                            v-on:recover-hidden-page="openRecoverHiddenPageModal"
                            v-on:edit-extra-content="openEditExtraContentModal"></MyPageSummary>
            </div>
          </draggable>

          <div class="card-row">
            <PageAddBlock class="hide-tooltip"></PageAddBlock>
          </div>
        </div>
      </div>
    </div>

    <div slot="modal-area">
      <BasicModal id="delete-my-card-modal" v-bind:title="$t('common.modal.remove.title')" v-on:ok="deleteCard">
        <div v-html="$t('common.modal.remove.message')"></div>
      </BasicModal>

      <BasicModal id="delete-my-card-page-modal" v-bind:title="$t('common.modal.remove.title')" v-on:ok="removePage" v-on:cancel="cancelRemovePage">
        <div v-html="$t('common.modal.remove.message')"></div>
      </BasicModal>

      <BasicModal id="hidden-my-card-page-modal" v-bind:title="'숨김'" v-on:ok="hiddenPage" v-on:cancel="cancelHiddenPage">
        <div v-html="'페이지를 숨김처리 하시겠습니까?'"></div>
      </BasicModal>

      <BasicModal id="recover-hidden-my-card-page-modal" v-bind:title="'숨김 처리 복구'" v-on:ok="recoverHiddenPage" v-on:cancel="cancelRecoverHiddenPage">
        <div v-html="'페이지를 숨김 상태를 복구하시겠습니까?'"></div>
      </BasicModal>

      <BasicModal id="export-content" v-bind:title="'내보내기'" v-on:ok="exportContent">
        <div v-html="'BEST 시스템으로 내보내겠습니까?'"></div>
      </BasicModal>

      <BasicModal id="make-card-public-modal" v-bind:title="$t('common.modal.make_public.title')" v-on:ok="updateShareLevel(1)">
        <div v-html="$t('common.modal.make_public.message')"></div>
      </BasicModal>

      <BasicModal id="make-card-private-modal" v-bind:title="$t('common.modal.make_private.title')" v-on:ok="updateShareLevel(2)">
        <div v-html="$t('common.modal.make_private.message')"></div>
      </BasicModal>

      <b-modal id="no-class-to-assign-alert"
               class="basic-modal no-header-modal hide-cancel-btn"
               v-bind:hide-header="true"
               v-bind:ok-title="$t('common.confirm')">
        <div v-html="$t('common.modal.no_class_to_assign_alert.message')"></div>
      </b-modal>

      <b-modal id="empty-card-info-alert"
               class="basic-modal no-header-modal hide-cancel-btn"
               v-bind:hide-header="true"
               v-bind:ok-title="$t('common.confirm')">
        <div v-html="$t('common.modal.empty_card_info.message')"></div>
      </b-modal>

      <b-modal id="quiz-type-select-modal"
               class="basic-modal hide-ok-btn square-area-btn-modal"
               v-bind:title="$t('card_details.create_page')"
               v-bind:close-title="$t('common.close')">
        <div class="row square-area-btn-row">
          <div class="col-3 square-area-btn-col">
            <div class="square-area-btn text-center typography--body-3 typography--color-secondary clickable"
                 v-on:click="showNonQuizEditorModal = true;$root.$emit('hide::modal', 'quiz-type-select-modal')">
              <div class="square-area-btn-icon square-area-btn-non-quiz"></div>
              {{ $t('common.page_types.non_quiz') }}
            </div>
          </div>

          <div class="col-3 square-area-btn-col">
            <div class="square-area-btn text-center typography--body-3 typography--color-secondary clickable"
                 v-on:click="showMultipleChoiceEditorModal = true;$root.$emit('hide::modal', 'quiz-type-select-modal')">
              <div class="square-area-btn-icon square-area-btn-multiple-choice"></div>
              {{ $t('common.page_types.multiple_choice') }}
            </div>
          </div>

          <div class="col-3 square-area-btn-col">
            <div class="square-area-btn text-center typography--body-3 typography--color-secondary clickable"
                 v-on:click="showTextEntryEditorModal = true;$root.$emit('hide::modal', 'quiz-type-select-modal')">
              <div class="square-area-btn-icon square-area-btn-text-entry"></div>
              {{ $t('common.page_types.text_entry') }}
            </div>
          </div>

          <div class="col-3 square-area-btn-col">
            <div class="square-area-btn text-center typography--body-3 typography--color-secondary clickable"
                 v-on:click="showEssayTypeEditorModal = true;$root.$emit('hide::modal', 'quiz-type-select-modal')">
              <div class="square-area-btn-icon square-area-btn-essay-type"></div>
              {{ $t('common.page_types.essay_type') }}
            </div>
          </div>
        </div>
      </b-modal>

      <ExtraContentEditModal
        id="extra-content-edit-modal"
        v-if="!!pageToEditExtraContent"
        v-on:cancel="cancelPageEdit"
        v-on:ok="cancelPageEdit"
        :page="pageToEditExtraContent"
        :card="card"
      />

      <PageLibraryModal v-on:import-pages="importPages"></PageLibraryModal>

      <PageEditorModal id="non_quiz-editor-modal"
                        v-show="showNonQuizEditorModal"
                        new-page-type="non_quiz"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showNonQuizEditorModal"
                        v-on:cancel="showNonQuizEditorModal = false">
      </PageEditorModal>

      <PageEditorModal id="multiple_choice-editor-modal"
                        v-show="showMultipleChoiceEditorModal"
                        new-page-type="multiple_choice"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showMultipleChoiceEditorModal"
                        v-on:cancel="showMultipleChoiceEditorModal = false">
      </PageEditorModal>

      <PageEditorModal id="text_entry-editor-modal"
                        v-show="showTextEntryEditorModal"
                        new-page-type="text_entry"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showTextEntryEditorModal"
                        v-on:cancel="showTextEntryEditorModal = false">
      </PageEditorModal>

      <PageEditorModal id="essay_type-editor-modal"
                        v-show="showEssayTypeEditorModal"
                        new-page-type="essay_type"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showEssayTypeEditorModal"
                        v-on:cancel="showEssayTypeEditorModal = false">
      </PageEditorModal>


      <PageEditorModal id="edit-page-editor-modal"
                        v-show="showEditPageModal"
                        v-bind:page="currentEditingPage"
                        v-on:page-added="onPageAdded"
                        v-bind:is-shown="showEditPageModal"
                        v-on:cancel="cancelPageEdit">
      </PageEditorModal>

      <AddVideoModal v-on:add="addVideo"></AddVideoModal>

      <MathEditorModal id="math-editor-modal" v-on:add="addMath"></MathEditorModal>

      <b-modal id="page-leave-alert"
               class="basic-modal no-header-modal"
               v-bind:hide-header="true"
               v-bind:close-title="$t('common.cancel')"
               v-bind:ok-title="$t('common.confirm')"
               v-on:ok="pageLeaveOk"
               v-on:cancel="pageLeaveCancel">
        <div v-html="$t('common.modal.page_leave_alert.message')"></div>
      </b-modal>

      <MyCardShareSetupModal
        v-if="isMyCardShareSetupModal"
        :cardId="selectedCardId"
        :cardShareLevel="savedCardShareLevel"
        :onModalCancelClick="() => this.isMyCardShareSetupModal = false"
        :onModalOkClick="handleCardShareLevelUpdate"
        :isLoading="isLoadingCardShareLevel"
        :isPublisher="isPublisherUser"
      />
    </div>

    <div slot="floating-action-menu" class="hidden-md-up">
      <FloatingDropup>
        <b-dropdown-item v-on:click="openCardPreview">{{ $t('common.preview') }}</b-dropdown-item>
        <div class="dropdown-divider"></div>
        <b-dropdown-item v-on:click="saveMyCard" v-bind:disabled="saveDisabled">{{ $t('common.save') }}</b-dropdown-item>
        <b-dropdown-item v-on:click="$root.$emit('show::modal', 'delete-my-card-modal')">{{ $t('common.card.menu.remove') }}</b-dropdown-item>
        <div class="dropdown-divider"></div>
        <b-dropdown-item v-on:click="openMakePublicModal" v-bind:disabled="saveDisabled">
          <span v-if="card.share_level === 2">{{ $t('common.card.menu.make_public') }}</span>
          <span v-if="card.share_level === 1" class="typography--color-accent">{{ $t('common.card.menu.make_private') }}</span>
        </b-dropdown-item>
      </FloatingDropup>
    </div>

  </MainLayout>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isEmpty, cloneDeep, reduce, isEqual, zipObject, extend, keyBy, keys, orderBy, differenceBy, sum, isNil, filter, merge } from 'lodash';
  import draggable from 'vuedraggable';
  import labels from '@/mixins/labels';
  import MyPageSummary from '../components/MyPageSummary';
  import PageAddBlock from '../components/PageAddBlock';
  import PageEditorModal from '../components/PageEditorModal';
  import AddVideoModal from '../components/AddVideoModal';
  import MathEditorModal from '../components/MathEditorModal';
  import MyCardShareSetupModal from '../components/MyCardShareSetupModal';
  import ExtraContentEditModal from '../../publishers/components/ExtraContentEditModal';

  export default {
    name: 'my-card-details',

    components: {
      PageEditorModal,
      MathEditorModal,
      MyPageSummary,
      AddVideoModal,
      PageAddBlock,
      draggable,
      MyCardShareSetupModal,
      ExtraContentEditModal,
    },

    mixins: [
      labels,
    ],

    data() {
      return {
        pagesReady: false,

        showNonQuizEditorModal: false,

        showMultipleChoiceEditorModal: false,

        showTextEntryEditorModal: false,

        showEssayTypeEditorModal: false,

        showEditPageModal: false,

        busy: false,

        hasError: false,

        pageIndexToDelete: null,

        pageToEditExtraContent: null,

        currentEditingPage: null,

        acceptableFileTypes: [
          'image/jpg',
          'image/jpeg',
          'image/png',
        ],

        fileSizeLimit: 10 * 1024 * 1024,

        card: {
          id: null,
          title: '',
          goal: null,
          pages: [],
          subject_id: undefined,
          grade_id: undefined,
          unit_id: undefined,
          chapter_position: undefined,
          chapter_name: undefined,
          core_concept_id: undefined,
          page_count: null,
          quiz_count: null,
          share_level: null,
          cover_image: null,
          cover_image_file: null,
        },

        originalCard: {},

        selected: {
          title: '',
          goal: '',
          subject_id: '9999',
          grade_id: '9999',
          unit_id: '9999',
          chapter_position: '9999',
          core_concept_id: '9999',
        },

        destination: null,
        isMyCardShareSetupModal: false,
        selectedCardId: null,
        savedCardShareLevel: null,
        isLoadingCardShareLevel: false,
      };
    },

    computed: {
      ...mapGetters([
        'activeEditorLocation',
        'isPublisherUser',
        'coreConceptList',
        'activeEditorId',
        'currentUserId',
        'coreConcepts',
        'subjectMap',
        'subjectIds',
        'gradeMap',
        'unitList',
        'myCards',
        'units',
        'videoCourseCard',
        'isLoadingVideoCourseCard',
        'gradeSubjects',
        'gradeList',
        'isBusanPublisher',
      ]),

      selectedSubjectGrade() {
        return `${this.selected.subject_id}&${this.selected.grade_id}`;
      },

      currentUnitList() {
        return this.unitList.filter(unit => (
          unit.subject_id === parseInt(this.selected.subject_id, 10) &&
          // unit.grade_id === parseInt(this.selected.grade_id, 10) &&
          unit.name !== 'NO NAME YET'
        ));
      },

      currentCoreConceptList() {
        return orderBy(this.coreConceptList.filter(coreConcept => (
          coreConcept.subject_id === parseInt(this.selected.subject_id, 10) &&
          coreConcept.grade_id === parseInt(this.selected.grade_id, 10)
        )), 'chapter_position', 'asc');
      },

      cardAuthorName() {
        if (this.card) {
          return this.card.author_name;
        }

        return '';
      },

      isModified() {
        const updatedProperties = this.getUpdatedProperties(this.card);
        delete updatedProperties.cover_image_file;

        return !isEmpty(updatedProperties);
      },

      styleBindings() {
        if (this.card && this.card.cover_image) {
          return {
            'background-image': `url('${this.card.cover_image}')`,
          };
        }

        return {};
      },

      saveDisabled() {
        return this.card.pages.length === 0 || this.hasError;
      },

      isVideoCourseCard() {
        return this.selected.video_course_id !== ''
          && this.isPublisherUser;
      },
    },

    watch: {
      gradeSubjects(state) {
        if (isEmpty(state)) {
          document.getElementById('subject-dropdown').firstElementChild.disabled = true;
        } else {
          document.getElementById('subject-dropdown').firstElementChild.disabled = false;
        }
      },

      currentCoreConceptList(state) {
        if (isEmpty(state)) {
          document.getElementById('core-concept-dropdown').firstElementChild.disabled = true;
        } else {
          document.getElementById('core-concept-dropdown').firstElementChild.disabled = false;
        }

        return !isEmpty(state);
      },

      currentUnitList(state) {
        if (isEmpty(state)) {
          document.getElementById('unit-dropdown').firstElementChild.disabled = true;
        } else {
          document.getElementById('unit-dropdown').firstElementChild.disabled = false;
        }

        return !isEmpty(state);
      },

      selectedSubjectGrade(state) {
        if (state.match(/^[0-9]+&9999$/) || state.match(/^9999&[0-9]+$/)) {
          return null;
        }

        if (!isNil(state.match(/[0-9]+&[0-9]+/))) {
          // if (this.getSchoolYear(this.gradeMap[this.selected.grade_id]) > 6) {
          //   if (isEmpty(this.currentCoreConceptList)) {
          //     return this.fetchCoreConcepts({
          //       subject_id: this.selected.subject_id,
          //       grade_id: this.selected.grade_id,
          //     });
          //   }

          //   return null;
          // }

          return Promise.all([
            this.fetchUnits({
              subject_id: this.selected.subject_id,
              grade_id: this.selected.grade_id,
            }),
            this.fetchCoreConcepts({
              subject_id: this.selected.subject_id,
              grade_id: this.selected.grade_id,
            }),
          ]);
        }

        return null;
      },
    },

    methods: {
      ...mapActions([
        'setSelectedPreviewPageIndex',
        'makeMyCardPrivateInStore',
        'uploadNewCardCoverImage',
        'removeMyCardCoverImage',
        'setSelectedPreviewCard',
        'uploadNewCardPageImage',
        'uploadNewCardPageAudio',
        'removeMyCardPageAudio',
        'removeMyCardPageImage',
        'setPageLibraryFilter',
        'createNewMyCardPage',
        'updateMyCardInStore',
        'fetchMyCardDetails',
        'updateMyCardPages',
        'fetchCoreConcepts',
        'fetchMyCardPages',
        'updateMyPage',
        'removeMyCard',
        'updateMyCard',
        'fetchUnits',
        'fetchVideoCourseCards',
        'updateVideoCourseCards',
        'createVideoCourseCards',
        'requestVideoCourseCard',
        'fetchGradeSubjects',
        'setMyCardShareLevel',
        'exportToBest',
      ]),

      onPageAdded(newQuizPage) {
        const newPageClone = cloneDeep(newQuizPage);

        if (this.pageIndexToDelete !== null) {
          this.card.pages.splice(this.pageIndexToDelete, 1, newPageClone);
          this.currentEditingPage = null;
          this.pageIndexToDelete = null;
        } else {
          newPageClone.position = this.card.pages.length + 1;
          this.card.pages.push(newPageClone);
        }

        this.showMultipleChoiceEditorModal = false;
        this.showTextEntryEditorModal = false;
        this.showEssayTypeEditorModal = false;
        this.showNonQuizEditorModal = false;
        this.showEditPageModal = false;
        this.updateCardPageCount();
      },

      addVideo(embed) {
        let content = '';
        if (embed.is_iframe) {
          content = `<p><iframe src="${embed.src}" width="640" height="360" frameborder="0" scrolling="no" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe></p><p></p>`;
        } else {
          content = `<p>동영상 링크</p><br>${embed.src}<p></p>`;
        }

        if (this.activeEditorId) {
          const editor = tinymce.get(this.activeEditorId);

          if (this.activeEditorLocation) {
            editor.selection.setRng(this.activeEditorLocation);
          }

          return editor.insertContent(content);
        }

        return tinymce.activeEditor.insertContent(content);
      },

      addMath(latex) {
        if (latex.length > 0) {
          const replacedLatex = latex.replace(/overarc/g, 'overparen');
          const mathInlineId = Math.floor(Math.random() * 1000);
          const content = `&nbsp;<span id="math-inline-${mathInlineId}" contenteditable="false">\\(${replacedLatex}\\)</span>&nbsp;`;
          let activeEditor;

          if (this.activeEditorId) {
            activeEditor = tinymce.get(this.activeEditorId);

            if (this.activeEditorLocation) {
              activeEditor.selection.setRng(this.activeEditorLocation);
            }
          } else {
            activeEditor = tinymce.activeEditor;
          }

          activeEditor.insertContent(content);

          setTimeout(() => {
            /* eslint-disable new-cap */
            MathJax.Hub.Queue(['Typeset', MathJax.Hub, `math-inline-${mathInlineId}`]);
            /* eslint-enable */
            activeEditor.insertContent('');
          }, 200);
        }
      },

      pageLeaveOk() {
        this.$router.push(this.destination.path);
      },

      pageLeaveCancel() {
        this.destination = null;
      },

      onCoverImageChange(event) {
        const images = event.target.files || event.dataTransfer.files;

        if (!images.length) {
          return null;
        }

        const file = images[0];

        if (!file || file.size === undefined || file.type === undefined) {
          return null;
        }

        if (file.size > this.fileSizeLimit) {
          // alert file size
          return this.$root.$emit('show::modal', 'file-size-alert');
        }

        if (this.acceptableFileTypes.indexOf(file.type) === -1) {
          // alert wrong file type
          return this.$root.$emit('show::modal', 'file-type-alert');
        }

        return this.createMedia(file, 'image');
      },

      createMedia(file) {
        if (!window.FileReader) {
          return this.$root.$emit('show::modal', 'file-reader-unsupported-alert');
        }

        const reader = new window.FileReader();

        reader.onload = (event) => {
          this.card.cover_image = event.target.result;
          this.card.cover_image_file = file;
        };

        return reader.readAsDataURL(file);
      },

      selectSubject(subjectId) {
        if (this.selected.subject_id !== subjectId) {
          this.selected.subject_id = subjectId;
          this.selected.core_concept_id = '9999';
          this.selected.unit_id = '9999';
        }
      },

      selectGrade(gradeId, schoolYear) {
        if (this.selected.grade_id !== gradeId) {
          this.selected.grade_id = gradeId;
          this.selected.core_concept_id = '9999';
          this.selected.unit_id = '9999';
          this.selected.subject_id = '9999';
          this.fetchGradeSubjects({ grade: schoolYear });
        }
      },

      selectUnit(unitId) {
        if (this.selected.unit_id !== unitId) {
          this.selected.unit_id = unitId;

          const unit = this.units[unitId];

          if (!isNil(unit) && !isNil(unit.core_concept_id)) {
            this.selected.core_concept_id = unit.core_concept_id;
          }
        }
      },

      selectCoreConcept(coreConceptId) {
        if (this.selected.core_concept_id !== coreConceptId) {
          this.selected.core_concept_id = coreConceptId;
        }
      },

      applySelectedCardInfo() {
        this.card.title = this.selected.title;
        this.card.goal = this.selected.goal || null;

        if (this.selected.subject_id !== '9999') {
          this.card.subject_id = this.selected.subject_id;
        } else {
          this.card.subject_id = undefined;
        }

        this.setPageLibraryFilter({ property: 'subject_id', value: this.card.subject_id || '0' });

        if (this.selected.grade_id !== '9999') {
          this.card.grade_id = this.selected.grade_id;
        } else {
          this.card.grade_id = undefined;
        }

        this.setPageLibraryFilter({ property: 'grade_id', value: this.card.grade_id || '0' });

        if (this.selected.unit_id !== '9999') {
          this.card.unit_id = this.selected.unit_id;

          const unit = this.units[this.card.unit_id];

          this.card.chapter_position = unit ? unit.position : undefined;
          this.card.chapter_name = unit ? unit.name : undefined;
        } else {
          this.card.chapter_position = undefined;
          this.card.chapter_name = undefined;
          this.card.unit_id = undefined;
        }

        if (this.selected.core_concept_id !== '9999') {
          this.card.core_concept_id = this.selected.core_concept_id;
        } else {
          this.card.core_concept_id = undefined;
        }

        if (this.card.unit_id) {
          this.setPageLibraryFilter({ property: 'unit_id', value: this.card.unit_id });
        } else {
          this.setPageLibraryFilter({ property: 'core_concept_id', value: this.card.core_concept_id || '0' });
        }

        if (
          this.isVideoCourseCard
          && !isEmpty(this.videoCourseCard)
        ) {
          this.requestVideoCourseCard();
          this.updateVideoCourseCards({
            id: this.videoCourseCard.id,
            video_course_id: this.selected.video_course_id,
            card_id: this.videoCourseCard.card_id,
          });
        }

        if (
          this.isVideoCourseCard
          && isEmpty(this.videoCourseCard)
          && !isEmpty(this.card)
          && Number(this.selected.video_course_id) > 0
        ) {
          this.requestVideoCourseCard();
          this.createVideoCourseCards({
            video_course_id: Number(this.selected.video_course_id),
            card_id: Number(this.card.id),
          });
        }
      },

      setupEditCardModal() {
        if (this.card.title) {
          this.selected.title = this.card.title;
        } else {
          this.selected.title = '';
        }

        if (this.card.goal) {
          this.selected.goal = this.card.goal;
        } else {
          this.selected.goal = '';
        }

        if (this.card.subject_id) {
          this.selected.subject_id = this.card.subject_id;
        } else {
          this.selected.subject_id = '9999';
        }

        if (this.card.grade_id !== undefined) {
          this.selected.grade_id = this.card.grade_id;
        } else {
          this.selected.grade_id = '9999';
        }

        if (this.card.unit_id) {
          this.selected.unit_id = this.card.unit_id;
        } else {
          this.selected.unit_id = '9999';
        }

        if (this.card.core_concept_id) {
          this.selected.core_concept_id = this.card.core_concept_id;
        } else {
          this.selected.core_concept_id = '9999';
        }

        if (this.isPublisherUser && !isEmpty(this.videoCourseCard)) {
          this.selected.video_course_id = this.videoCourseCard.video_course_id;
        }
      },

      getLearningElements(coreConcept) {
        if (!coreConcept) {
          return null;
        }

        return coreConcept.description;
      },

      importPages(pageList) {
        this.card.pages.push(...cloneDeep(pageList));
        this.updateCardPageCount();
      },

      removePage() {
        if (this.pageIndexToDelete !== null) {
          this.card.pages.splice(this.pageIndexToDelete, 1);
          this.updateCardPageCount();
          this.pageIndexToDelete = null;
        }
      },

      hiddenPage() {
        const page = this.card.pages[this.pageIndexToHidden];
        page.share_level = 99;
        this.updateCardPageCount();
      },

      recoverHiddenPage() {
        const page = this.card.pages[this.pageIndexToRecoverHidden];
        page.share_level = this.card.share_level;
        this.updateCardPageCount();
      },

      async exportContent() {
        this.busy = true;
        await this.exportToBest(this.card);
        this.busy = false;
      },

      updateCardPageCount() {
        const availablePages = filter(this.card.pages, page => (
          page.share_level !== 99
        ));
        const imageCount = sum(availablePages.map(page => (page.image_count || 0)));
        const audioCount = sum(availablePages.map(page => (page.audio_count || 0)));
        const videoCount = sum(availablePages.map(page => (page.video_count || 0)));

        this.card.page_count = availablePages.length;
        this.card.quiz_count = availablePages.filter(page => (!!page.is_quiz)).length;
        this.card.image_count = imageCount;
        this.card.audio_count = audioCount;
        this.card.video_count = videoCount;
      },

      cancelRemovePage() {
        this.pageIndexToDelete = null;
      },

      cancelHiddenPage() {
        this.pageIndexToHidden = null;
      },

      cancelRecoverHiddenPage() {
        this.pageIndexToRecoverHidden = null;
      },

      openRemovePageModal(pageIndex) {
        this.pageIndexToDelete = pageIndex;

        this.$root.$emit('show::modal', 'delete-my-card-page-modal');
      },

      openHiddenPageModal(pageIndex) {
        this.pageIndexToHidden = pageIndex;

        this.$root.$emit('show::modal', 'hidden-my-card-page-modal');
      },

      openRecoverHiddenPageModal(pageIndex) {
        this.pageIndexToRecoverHidden = pageIndex;

        this.$root.$emit('show::modal', 'recover-hidden-my-card-page-modal');
      },

      openEditExtraContentModal(pageIndex) {
        if (this.isModified) {
          alert('수정 중인 문항이 있어, 긴지문을 추가할 수 없는 상태입니다. (새로고침 후 추가 가능)');
        } else {
          this.pageToEditExtraContent = this.card.pages[pageIndex];
        }
      },

      openPageEditorModal(pageIndex) {
        this.currentEditingPage = this.card.pages[pageIndex];
        this.pageIndexToDelete = pageIndex;

        this.showEditPageModal = true;
      },

      cancelPageEdit() {
        this.showEditPageModal = false;
        this.currentEditingPage = null;
        this.pageIndexToDelete = null;
        this.pageToEditExtraContent = null;
      },

      openMakePublicModal() {
        if (this.card.share_level === 1) {
          // make private
          return this.$root.$emit('show::modal', 'make-card-private-modal');
        }

        if (this.isModified) {
          // open modal
          this.$root.$emit('show::modal', 'make-card-public-modal');

          return Promise.resolve();
        }

        // make public
        return this.updateShareLevel(1);
      },

      updateShareLevel(shareLevel) {
        if (shareLevel === 1) {
          this.card.share_level = 1;

          return this.saveMyCard();
        }

        const updateInfo = { share_level: 2 };

        return this.updateMyCard({ cardId: this.card.id, updateInfo })
          .then(() => {
            this.card.share_level = 2;

            return this.makeMyCardPrivateInStore(this.card.id);
          })
          .then(() => {
            if (this.originalCard.share_level === 1) {
              this.originalCard.share_level = 2;
            }
          })
          .catch((err) => {
            this.$root.$emit('show::modal', 'basic-error-alert');
            return Promise.reject(err);
          });
      },

      applyCardTitle(inputText) {
        this.card.title = inputText;
      },

      onHasError(hasError) {
        this.hasError = hasError;
      },

      deleteCard() {
        this.removeMyCard(this.card.id)
          .then(() => {
            this.$router.push('/my-cards');
          })
          .catch((err) => {
            this.$root.$emit('show::modal', 'basic-error-alert');
            return Promise.reject(err);
          });
      },

      copyCardProperties(card) {
        const cardClone = cloneDeep(card);
        extend(this.card, cardClone);
      },

      openCardPreview() {
        const cardClone = cloneDeep(this.card);

        this.setSelectedPreviewCard(cardClone)
          .then(() => (
            this.setSelectedPreviewPageIndex(0)
          ))
          .then(() => {
            this.$root.$emit('show::modal', 'card-preview-modal');
          });
      },

      getUpdatedProperties() {
        const updatedFields = reduce(this.card, (result, value, key) => (
          isEqual(value, this.originalCard[key])
            ? result
            : result.concat(key)
        ), []);

        return zipObject(updatedFields, updatedFields.map(key => this.card[key]));
      },

      saveMyCard() {
        if (!this.busy) {
          this.busy = true;
          this.updateCardPageCount();
          const updateInfo = this.getUpdatedProperties();
          let updated = false;

          // TODO handle error case
          return Promise.resolve()
            .then(() => {
              // 1. delete & upload cover image if needed
              if (!updateInfo.cover_image || !updateInfo.cover_image_file) {
                delete updateInfo.cover_image_file;
                return true;
              }

              const coverImageInfo = {
                cardId: this.card.id,
                imageFile: updateInfo.cover_image_file,
              };

              if (!this.originalCard.cover_image) {
                return this.uploadNewCardCoverImage(coverImageInfo)
                  .then(() => {
                    delete updateInfo.cover_image;
                    delete updateInfo.cover_image_file;
                    this.card.cover_image_file = null;
                    updated = true;
                  });
              }

              return this.removeMyCardCoverImage(this.card.id)
                .then(() => (
                  this.uploadNewCardCoverImage(coverImageInfo)
                    .then(() => {
                      delete updateInfo.cover_image;
                      delete updateInfo.cover_image_file;
                      this.card.cover_image_file = null;
                      updated = true;
                    })
                ));
            })
            .then(() => {
              // 2. update card pages if needed
              if (!updateInfo.pages) {
                return true;
              }

              const pageUpdateList = [];
              const originalCardPages = keyBy(this.originalCard.pages, 'id');

              this.card.pages.forEach((page, i) => {
                // 2.1 add updated card pages
                if (!page.card_page_count && originalCardPages[page.id]) {
                  if (originalCardPages[page.id].content !== page.content ||
                      originalCardPages[page.id].description !== page.description ||
                      originalCardPages[page.id].answer_view_type !== page.answer_view_type ||
                      originalCardPages[page.id].grading_keywords !== page.grading_keywords ||
                      originalCardPages[page.id].score !== page.score ||
                      originalCardPages[page.id].answer_criteria !== page.answer_criteria ||
                      originalCardPages[page.id].share_level !== page.share_level ||
                      !isEqual(originalCardPages[page.id].answers, page.answers) ||
                      !isEqual(originalCardPages[page.id].audios, page.audios)
                    ) {
                    pageUpdateList.push({
                      ...page,

                      position: i + 1,
                      type: 'edited',
                    });
                  } else if (originalCardPages[page.id].position !== (i + 1)) {
                    pageUpdateList.push({
                      page_id: page.id,
                      position: i + 1,
                      type: 'update',
                    });
                  }
                  delete originalCardPages[page.id];
                } else {
                  // 2.2 add newly added card pages
                  /* eslint-disable no-lonely-if */
                  if (page.id) {
                    pageUpdateList.push({
                      store_page_id: page.id,
                      position: i + 1,
                      type: 'new',
                    });
                  } else {
                    pageUpdateList.push({
                      ...page,

                      position: i + 1,
                      type: 'created',
                    });
                  }
                  /* eslint-enable */
                }
              });

              keys(originalCardPages).forEach((pageId) => {
                // 2.3 add removed card pages
                pageUpdateList.push({
                  page_id: pageId,
                  type: 'delete',
                });
              });

              const createdPageList = pageUpdateList.filter(page => (page.type === 'created'));
              const editedPageList = pageUpdateList.filter(page => (page.type === 'edited'));
              const pageUpdateInfo = {
                cardId: this.card.id,
                pageUpdateList: pageUpdateList.filter(page => (
                  page.type !== 'created' && page.type !== 'edited'
                )),
              };

              return this.updateMyCardPages(pageUpdateInfo)
                .then(() => {
                  // 3. create new pages
                  /* eslint-disable max-len */
                  const createPagePromises = createdPageList.map(page => (
                    this.createNewMyCardPage({ cardId: this.card.id, page: { ...page, images: [] } })
                      .then((newPage) => {
                        const createdUpdateInfo = {
                          content: newPage.content,
                          description: newPage.description || '',
                        };

                        // 3.1 upload page images
                        const uploadImgPromises = page.images.map(image => (
                          this.uploadNewCardPageImage({ pageId: newPage.id, imageFile: image.image_file })
                            .then((newImage) => {
                              if (createdUpdateInfo.content.indexOf(`data-image-id="${image.key}"`) !== -1) {
                                createdUpdateInfo.content = createdUpdateInfo.content
                                  .replace(`data-image-id="${image.key}"`, `data-image-id="${newImage.id}"`)
                                  .replace(image.image_url, newImage.image_url);
                              }

                              if (createdUpdateInfo.description.indexOf(`data-image-id="${image.key}"`) !== -1) {
                                createdUpdateInfo.description = createdUpdateInfo.description
                                  .replace(`data-image-id="${image.key}"`, `data-image-id="${newImage.id}"`)
                                  .replace(image.image_url, newImage.image_url);
                              }
                            })
                        ));

                        return Promise.all(uploadImgPromises)
                          .then(() => (
                            Promise.all(page.audios.map(audio => (
                              this.uploadNewCardPageAudio({ pageId: newPage.id, audioFile: audio.audio_file })
                            )))
                          ))
                          .then(() => {
                            if (newPage.content === createdUpdateInfo.content &&
                                newPage.description === createdUpdateInfo.description) {
                              return Promise.resolve();
                            }

                            // 3.3 update page content
                            return this.updateMyPage({ pageId: newPage.id, updateInfo: createdUpdateInfo });
                          });
                      })
                  ));
                  /* eslint-enable */

                  return Promise.all(createPagePromises);
                })
                .then(() => {
                  // 4. update edited pages
                  const oldCardPages = keyBy(this.originalCard.pages, 'id');

                  const editedPagePromises = editedPageList.map((page) => {
                    const newImages = page.images.filter(img => (!img.id));
                    const newAudios = page.audios.filter(audio => (!audio.id));
                    const removedImages = differenceBy(oldCardPages[page.id].images, page.images, 'id');
                    const removedAudios = differenceBy(oldCardPages[page.id].audios, page.audios, 'id');
                    const editedUpdateInfo = {
                      content: page.content,
                      description: page.description || '',
                      answers: page.answers,
                      grading_keywords: page.grading_keywords,
                      score: page.score,
                      answer_criteria: page.answer_criteria,
                      share_level: page.share_level,
                    };

                    // 4.1 upload new images
                    const uploadImgPromises = newImages.map(image => (
                      this.uploadNewCardPageImage({ pageId: page.id, imageFile: image.image_file })
                        .then((newImage) => {
                          if (editedUpdateInfo.content.indexOf(`data-image-id="${image.key}"`) !== -1) {
                            editedUpdateInfo.content = editedUpdateInfo.content
                              .replace(`data-image-id="${image.key}"`, `data-image-id="${newImage.id}"`)
                              .replace(image.image_url, newImage.image_url);
                          }

                          if (editedUpdateInfo.description.indexOf(`data-image-id="${image.key}"`) !== -1) {
                            editedUpdateInfo.description = editedUpdateInfo.description
                              .replace(`data-image-id="${image.key}"`, `data-image-id="${newImage.id}"`)
                              .replace(image.image_url, newImage.image_url);
                          }
                        })
                    ));

                    // 4.2 delete removed images
                    const removeImgPromises = removedImages.map(image => (
                      this.removeMyCardPageImage(image.id)
                    ));

                    // 4.3 upload new audios
                    const uploadAudioPromises = newAudios.map(audio => (
                      this.uploadNewCardPageAudio({ pageId: page.id, audioFile: audio.audio_file })
                    ));

                    // 4.4 delete removed audios
                    const removeAudioPromises = removedAudios.map(audio => (
                      this.removeMyCardPageAudio(audio.id)
                    ));

                    // update page position
                    if (oldCardPages[page.id].position !== page.position) {
                      editedUpdateInfo.position = page.position;
                    }

                    // update page answer_view_type
                    if (oldCardPages[page.id].answer_view_type !== page.answer_view_type) {
                      editedUpdateInfo.answer_view_type = page.answer_view_type;
                    }

                    // update page image_count
                    if (oldCardPages[page.id].image_count !== page.image_count) {
                      editedUpdateInfo.image_count = page.image_count;
                    }

                    // update page audio_count
                    if (oldCardPages[page.id].audio_count !== page.audio_count) {
                      editedUpdateInfo.audio_count = page.audio_count;
                    }

                    return Promise.all(uploadImgPromises)
                      .then(() => (
                        Promise.all(removeImgPromises)
                      ))
                      .then(() => (
                        Promise.all(uploadAudioPromises)
                      ))
                      .then(() => (
                        Promise.all(removeAudioPromises)
                      ))
                      .then(() => (
                        // 4.5 update page
                        this.updateMyPage({ pageId: page.id, updateInfo: editedUpdateInfo })
                      ));
                  });

                  return Promise.all(editedPagePromises);
                })
                .then(() => {
                  delete updateInfo.pages;
                  updated = true;
                });
            })
            .then(() => {
              // 5. update card if needed
              if (isEmpty(updateInfo)) {
                return true;
              }

              return this.updateMyCard({ cardId: this.card.id, updateInfo })
                .then(() => {
                  updated = true;
                });
            })
            .then(() => {
              // 6. fetch updated card details
              if (!updated) {
                return true;
              }

              return this.fetchMyCardDetails(this.card.id)
                .then((card) => {
                  this.originalCard = card;
                  this.copyCardProperties(this.originalCard);

                  // 7. fetch updated card pages
                  return this.fetchMyCardPages({
                    cardId: card.id,
                    isPublisherUser: this.isPublisherUser,
                  });
                })
                .then((pages) => {
                  this.originalCard.pages = pages;
                  this.card.pages = cloneDeep(this.originalCard.pages);

                  return true;
                })
                .then(() => (
                  // 8. update card in store
                  this.updateMyCardInStore(cloneDeep(this.originalCard))
                ));
            })
            .then(() => {
              this.busy = false;
            })
            .catch((err) => {
              this.busy = false;
              this.$root.$emit('show::modal', 'basic-error-alert');
              return Promise.reject(err);
            });
        }

        return Promise.resolve();
      },

      handleMyCardShareSetupClick(cardId, cardShareLevel) {
        if (this.isModified) {
          this.$root.$emit('show::modal', 'make-card-public-modal');
        }

        this.selectedCardId = cardId;
        this.savedCardShareLevel = cardShareLevel;
        this.isMyCardShareSetupModal = true;
      },

      handleCardShareLevelUpdate(selectedMyCardShareLevel) {
        const updateInfo = { share_level: selectedMyCardShareLevel };

        this.originalCard.pages.forEach((page) => {
          if (page.share_level !== 99) {
            merge(page, { share_level: selectedMyCardShareLevel });
          }
        });
        this.card.pages.forEach((page) => {
          if (page.share_level !== 99) {
            merge(page, { share_level: selectedMyCardShareLevel });
          }
        });

        this.isLoadingCardShareLevel = true;
        this.updateMyCard({ cardId: this.selectedCardId, updateInfo })
          .then(() => this.setMyCardShareLevel({
            cardId: this.selectedCardId,
            shareLevel: selectedMyCardShareLevel,
          }))
          .catch((err) => {
            this.$root.$emit('show::modal', 'basic-error-alert');
            return Promise.reject(err);
          })
          .finally(() => {
            this.originalCard.share_level = selectedMyCardShareLevel;
            this.card.share_level = selectedMyCardShareLevel;
            this.isLoadingCardShareLevel = false;
            this.isMyCardShareSetupModal = false;
          });
      },
    },

    beforeRouteLeave(to, from, next) {
      if (!this.destination && this.isModified) {
        this.destination = to;
        this.$root.$emit('show::modal', 'page-leave-alert');

        return next(false);
      }

      return next();
    },

    mounted() {
      if (isEmpty(this.gradeSubjects)) {
        document.getElementById('subject-dropdown').firstElementChild.disabled = true;
      } else {
        document.getElementById('subject-dropdown').firstElementChild.disabled = false;
      }

      if (isEmpty(this.currentCoreConceptList)) {
        document.getElementById('core-concept-dropdown').firstElementChild.disabled = true;
      } else {
        document.getElementById('core-concept-dropdown').firstElementChild.disabled = false;
      }

      if (isEmpty(this.currentUnitList)) {
        document.getElementById('unit-dropdown').firstElementChild.disabled = true;
      } else {
        document.getElementById('unit-dropdown').firstElementChild.disabled = false;
      }
    },

    created() {
      const cardId = this.$route.params.card_id;

      if (this.myCards[cardId]) {
        this.originalCard = this.myCards[cardId];
        this.copyCardProperties(this.originalCard);

        if (!isNil(this.originalCard.core_concept)) {
          this.fetchCoreConcepts({
            subject_id: this.originalCard.core_concept.subject_id,
            grade_id: this.originalCard.core_concept.grade_id,
          });
        }
      }

      if (isEmpty(this.card) || !this.card.id) {
        this.fetchMyCardDetails(cardId)
          .then((card) => {
            if (card.author_id !== this.currentUserId) {
              return Promise.reject('not_my_card');
            }

            this.originalCard = card;
            this.copyCardProperties(this.originalCard);

            this.fetchGradeSubjects({ grade: this.originalCard.grade_id });

            if (!isNil(card.core_concept)) {
              this.fetchCoreConcepts({
                subject_id: this.originalCard.core_concept.subject_id,
                grade_id: this.originalCard.core_concept.grade_id,
              });
            }
            return this.fetchMyCardPages({
              cardId: card.id,
              isPublisherUser: this.isPublisherUser,
            });
          })
          .then((pages) => {
            this.originalCard.pages = pages;
            this.card.pages = cloneDeep(this.originalCard.pages);

            return null;
          })
          .then(() => {
            this.pagesReady = true;
          })
          .then(() => (
            this.setPageLibraryFilter({ property: 'subject_id', value: this.card.subject_id || '0' })
          ))
          .then(() => (
            this.setPageLibraryFilter({ property: 'grade_id', value: this.card.grade_id || '0' })
          ))
          .then(() => (
            this.setPageLibraryFilter({ property: 'core_concept_id', value: this.card.core_concept_id || '0' })
          ))
          .catch((err) => {
            if (err === 'not_my_card') {
              this.destination = { path: '/my-cards' };
              return this.$router.replace('/my-cards');
            }

            this.$root.$emit('show::modal', 'basic-error-alert');
            // this.$router.push('/my-cards');
            return Promise.reject(err);
          });
      } else if (isEmpty(this.card.pages)) {
        this.fetchGradeSubjects({ grade: this.card.grade_id })
          .then(() => this.fetchMyCardPages({
            cardId: this.card.id,
            isPublisherUser: this.isPublisherUser,
          }))
          .then(() => {
            this.card.pages = cloneDeep(this.originalCard.pages);

            return null;
          })
          .then(() => {
            this.pagesReady = true;
          })
          .catch((err) => {
            this.$root.$emit('show::modal', 'basic-error-alert');
            return Promise.reject(err);
          });
      } else {
        this.pagesReady = true;
      }

      if (cardId && this.isPublisherUser) {
        const params = {
          card_id: cardId,
        };

        this.fetchVideoCourseCards(params);
      }
    },
  };
</script>



// WEBPACK FOOTER //
// src/app/my-cards/views/MyCardDetails.vue